import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Room } from '../../types';
import api from '../../services/api';
import CreateRoomModal from './CreateRoomModal';
import JoinRoomModal from './JoinRoomModal';

const RoomList: React.FC = () => {
  const [rooms, setRooms] = useState<Room[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showJoinModal, setShowJoinModal] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchRooms();
  }, []);

  const fetchRooms = async () => {
    try {
      setLoading(true);
      const response = await api.get('/rooms');
      setRooms(response.data.rooms || []);
      setError(null);
    } catch (err: any) {
      setError('Failed to load rooms');
      console.error('Error fetching rooms:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRoomCreated = (roomId: string) => {
    navigate(`/rooms/${roomId}/lobby`);
  };

  const handleRoomJoined = (roomId: string) => {
    navigate(`/rooms/${roomId}/lobby?autoJoin=true`);
  };

  const handleJoinRoom = async (roomId: string) => {
    // Navigate to lobby with autoJoin flag
    // The lobby will establish WebSocket connection first, then join
    navigate(`/rooms/${roomId}/lobby?autoJoin=true`);
  };

  const handleRejoinRoom = async (roomId: string) => {
    // Navigate directly to game page for rejoin - the game page will handle reconnection
    navigate(`/rooms/${roomId}/game?rejoin=true`);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <div className="room-list-container">
        <div className="loading">
          <div className="loading-spinner"></div>
          Loading rooms...
        </div>
      </div>
    );
  }

  return (
    <div className="room-list-container">
      <div className="room-list-header">
        <div className="room-actions">
          <button
            className="btn btn-primary"
            onClick={() => setShowCreateModal(true)}
          >
            ✨ Create Room
          </button>
          <button
            className="btn btn-secondary"
            onClick={() => setShowJoinModal(true)}
          >
            🎯 Join Room
          </button>
          <button
            className="btn btn-success"
            onClick={fetchRooms}
          >
            🔄 Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      <div className="rooms-grid">
        {rooms.length === 0 ? (
          <div className="no-rooms">
            <p>No rooms available. Create a new room to get started!</p>
          </div>
        ) : (
          rooms.map((room) => (
            <div key={room.id} className="room-card">
              <div className="room-header">
                <h3>Room {room.id.slice(0, 8)}</h3>
                <span className={`room-status ${room.state}`}>
                  {room.state}
                </span>
              </div>

              <div className="room-info">
                <p>
                  Players: {room.players.length}/{room.maxPlayers}
                </p>
                <p>Created: {formatDate(room.createdAt)}</p>
              </div>

              <div className="room-players">
                {room.players.slice(0, 3).map((player, index) => (
                  <span key={index} className="player-name">
                    {player.username}
                  </span>
                ))}
                {room.players.length > 3 && (
                  <span className="more-players">
                    +{room.players.length - 3} more
                  </span>
                )}
              </div>

              <div className="room-actions">
                {room.canRejoin ? (
                  <button
                    className="join-button rejoin-button"
                    onClick={() => handleRejoinRoom(room.id)}
                  >
                    🔄 Rejoin Game
                  </button>
                ) : room.state === 'waiting' && room.players.length < room.maxPlayers ? (
                  <button
                    className="join-button"
                    onClick={() => handleJoinRoom(room.id)}
                  >
                    Join Room
                  </button>
                ) : (
                  <button className="join-button" disabled>
                    {room.state === 'in-progress' ? 'In Progress' : 'Full'}
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      <CreateRoomModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onRoomCreated={handleRoomCreated}
      />

      <JoinRoomModal
        isOpen={showJoinModal}
        onClose={() => setShowJoinModal(false)}
        onRoomJoined={handleRoomJoined}
      />
    </div>
  );
};

export default RoomList;